/**
 * A DataLoader-style request coalescer: collect keys requested within a short
 * window, fetch them in one batched call, then fan the results back out per key.
 *
 * This exists so callers can stay per-entity — one react-query entry per id, so
 * an id already in cache is never re-requested — without that meaning one HTTP
 * request per entity. The batching lives here; the cache keys stay natural.
 *
 * Deliberately a factory rather than a module-level singleton: the app holds one
 * instance per endpoint at module scope, but tests can build an isolated loader
 * per case instead of depending on a shared queue being drained between them.
 */
import { toError } from "~/error-handling";

interface PendingLoad<T> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

export interface BatchLoader<T> {
  /**
   * Fetch one key, batched with every other key requested in the same window.
   * Rejects if the batch fails, or if the response carries no value for this key.
   */
  load: (key: string) => Promise<T>;
}

export interface BatchLoaderOptions<T> {
  /**
   * Fetch many keys at once. Return a value per key the response could answer
   * for; a key left OUT of the map is treated as unanswered and rejects. A key
   * mapped to a value (including `null`, where `T` permits it) resolves — which
   * is how a caller expresses "the server answered, and the answer is nothing"
   * as data rather than as a failure.
   */
  fetchBatch: (keys: string[]) => Promise<Map<string, T>>;
  /** How long to collect keys before flushing. */
  windowMs: number;
  /** Rejection for a key the batch didn't answer for. */
  missingKeyError?: (key: string) => Error;
}

const defaultMissingKeyError = (key: string): Error =>
  new Error(`Batch load returned no value for ${key}`);

export function createBatchLoader<T>({
  fetchBatch,
  windowMs,
  missingKeyError = defaultMissingKeyError,
}: BatchLoaderOptions<T>): BatchLoader<T> {
  let pending = new Map<string, PendingLoad<T>[]>();
  let flushTimer: ReturnType<typeof setTimeout> | undefined;

  const flush = async (): Promise<void> => {
    // Take the queue before awaiting anything. Keys requested while this batch
    // is in flight must land in a fresh queue and get their own flush — if this
    // reset moved below the await, they would be silently absorbed into a batch
    // that had already left, and never settle.
    const flushing = pending;
    pending = new Map();
    flushTimer = undefined;

    let values: Map<string, T>;
    try {
      values = await fetchBatch([...flushing.keys()]);
    } catch (cause) {
      const error = toError(cause, "Batch load failed");
      for (const waiting of flushing.values()) {
        for (const { reject } of waiting) reject(error);
      }
      return;
    }

    for (const [key, waiting] of flushing) {
      const answered = values.has(key);
      const value = values.get(key) as T;
      for (const { resolve, reject } of waiting) {
        if (answered) resolve(value);
        else reject(missingKeyError(key));
      }
    }
  };

  return {
    load(key: string): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        const waiting = pending.get(key);
        if (waiting) {
          waiting.push({ resolve, reject });
        } else {
          pending.set(key, [{ resolve, reject }]);
        }
        if (flushTimer === undefined) {
          flushTimer = setTimeout(flush, windowMs);
        }
      });
    },
  };
}
